import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Upload, Loader2, Trash2, Link2, X, CheckCircle2, RefreshCw, FileSpreadsheet, FileDown, Archive, ArchiveRestore } from "lucide-react";
import { DateInputIT } from "@/components/DateInputIT";
import { fmtIT } from "@/lib/dateIT";

const euro = (n) => (Number(n) || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const ETICHETTE = ["Bonifico cliente", "POS/incasso vario", "Assegno incassato", "Giroconto in entrata", "Polizza", "Altra entrata",
  "Bonifico a compagnia/fornitore", "Imposte/bolli/commissioni", "Giroconto in uscita", "Prestito/finanziamento", "Rimborso/storno", "Altra uscita"];

export function RiconciliazioneTab() {
  const [batches, setBatches] = useState([]);
  const [sel, setSel] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadingAsy, setUploadingAsy] = useState(false);
  const [asyBatches, setAsyBatches] = useState([]);
  const [view, setView] = useState("abbinamento"); // abbinamento | movimenti
  const [dal, setDal] = useState("");
  const [al, setAl] = useState("");
  const inputRef = useRef();
  const asyInputRef = useRef();

  const periodoParam = () => {
    const p = new URLSearchParams();
    if (dal) p.append("dal", dal);
    if (al) p.append("al", al);
    const s = p.toString();
    return s ? `?${s}` : "";
  };

  const loadBatches = () => api.get("/contabilita/riconciliazione/batch").then((r) => { setBatches(r.data); if (!sel && r.data[0]) setSel(r.data[0].id); }).catch(() => setBatches([]));
  const loadAsy = () => api.get("/contabilita/riconciliazione/assieasy/batch").then((r) => setAsyBatches(r.data)).catch(() => setAsyBatches([]));
  useEffect(() => { loadBatches(); loadAsy(); }, []);

  const onUploadAsy = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setUploadingAsy(true);
    try {
      const { data } = await api.post(`/contabilita/riconciliazione/import-assieasy${periodoParam()}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importati ${data.totale} incassi Assieasy${data.saltati ? ` · ${data.saltati} già presenti scartati` : ""}`);
      loadAsy();
    } catch (err) { toast.error(err.response?.data?.detail || "Import Assieasy non riuscito"); }
    finally { setUploadingAsy(false); if (asyInputRef.current) asyInputRef.current.value = ""; }
  };
  const delAsy = async (id) => { if (!window.confirm("Eliminare gli incassi Assieasy importati?")) return; await api.delete(`/contabilita/riconciliazione/assieasy/batch/${id}`); loadAsy(); };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(`/contabilita/riconciliazione/import-banca${periodoParam()}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importati ${data.totale} movimenti (${data.n_entrate} entrate, ${data.n_uscite} uscite)${data.saltati ? ` · ${data.saltati} già presenti scartati` : ""}`);
      await loadBatches(); setSel(data.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Import non riuscito"); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const delBatch = async (id) => { if (!window.confirm("Eliminare l'import e i suoi movimenti?")) return; await api.delete(`/contabilita/riconciliazione/batch/${id}`); setSel(""); loadBatches(); };
  const archivia = async (id) => { await api.post(`/contabilita/riconciliazione/batch/${id}/archivia`); toast.success("Riconciliazione archiviata"); loadBatches(); };
  const riapri = async (id) => { await api.post(`/contabilita/riconciliazione/batch/${id}/riapri`); toast.success("Riconciliazione riaperta"); loadBatches(); };
  const batch = batches.find((b) => b.id === sel);
  const archived = batch?.stato === "archiviato";

  return (
    <div>
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-end gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1" data-testid="ric-periodo">
            <div><Label className="text-[10px] text-slate-500">Dal (opz.)</Label><DateInputIT value={dal} onChange={(v) => setDal(v)} className="w-36 h-8" testid="ric-dal" /></div>
            <div><Label className="text-[10px] text-slate-500">Al (opz.)</Label><DateInputIT value={al} onChange={(v) => setAl(v)} className="w-36 h-8" testid="ric-al" /></div>
            {(dal || al) && <button onClick={() => { setDal(""); setAl(""); }} className="h-8 text-xs text-slate-400 hover:text-red-600 self-center" title="Azzera periodo" data-testid="ric-periodo-reset"><X className="h-3.5 w-3.5" /></button>}
          </div>
          <input ref={inputRef} type="file" accept=".xlsx,.xls" onChange={onUpload} className="hidden" data-testid="ric-file-input" />
          <Button onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="ric-import-btn">
            {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}Importa estratto banca (Excel)
          </Button>
          {batches.length > 0 && (
            <Select value={sel} onValueChange={setSel}><SelectTrigger className="w-72" data-testid="ric-batch-select"><SelectValue placeholder="Seleziona import" /></SelectTrigger>
              <SelectContent>{batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.stato === "archiviato" ? "🗄 " : ""}{b.filename} · {b.periodo_dal} → {b.periodo_al}</SelectItem>)}</SelectContent></Select>
          )}
          <input ref={asyInputRef} type="file" accept=".csv" onChange={onUploadAsy} className="hidden" data-testid="ric-asy-input" />
          <Button variant="outline" onClick={() => asyInputRef.current?.click()} disabled={uploadingAsy} data-testid="ric-import-asy-btn">
            {uploadingAsy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1.5" />}Import incassi Assieasy (CSV)
          </Button>
          {asyBatches.length > 0 && (
            <Badge variant="secondary" className="bg-blue-100 text-blue-700 gap-1">
              {asyBatches.reduce((a, b) => a + b.totale, 0)} incassi Assieasy
              <button onClick={() => delAsy(asyBatches[0].id)} className="ml-1 hover:text-red-600" title="Elimina"><X className="h-3 w-3" /></button>
            </Badge>
          )}
        </div>
        {batch && (
          <div className="flex items-center gap-2">
            {archived && <Badge className="bg-emerald-100 text-emerald-700 gap-1"><Archive className="h-3 w-3" />Archiviata</Badge>}
            <a href={`${API}/contabilita/riconciliazione/batch/${batch.id}/export/excel`} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" data-testid="ric-export-excel"><FileDown className="h-4 w-4 mr-1" />Excel</Button>
            </a>
            <a href={`${API}/contabilita/riconciliazione/batch/${batch.id}/export/pdf`} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" data-testid="ric-export-pdf"><FileDown className="h-4 w-4 mr-1" />PDF</Button>
            </a>
            {archived
              ? <Button variant="outline" size="sm" onClick={() => riapri(batch.id)} data-testid="ric-riapri"><ArchiveRestore className="h-4 w-4 mr-1" />Riapri</Button>
              : <Button variant="outline" size="sm" onClick={() => archivia(batch.id)} data-testid="ric-archivia"><Archive className="h-4 w-4 mr-1" />Archivia</Button>}
            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => delBatch(batch.id)}><Trash2 className="h-4 w-4 mr-1" />Elimina</Button>
          </div>
        )}
      </div>

      {!batch ? (
        <div data-testid="ric-empty" className="text-center py-16 rounded-2xl border border-dashed border-slate-300 bg-white text-slate-500">
          <FileSpreadsheet className="h-8 w-8 mx-auto mb-2 text-slate-400" />
          Importa l'estratto conto bancario (.xlsx) per avviare la riconciliazione.
        </div>
      ) : (
        <div>
          <QuadraturaBadge batchId={batch.id} refreshKey={view} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Stat label="Movimenti" value={batch.totale} />
            <Stat label="Entrate" value={batch.n_entrate} sub={euro(batch.tot_entrate)} cls="text-emerald-600" />
            <Stat label="Uscite" value={batch.n_uscite} sub={euro(batch.tot_uscite)} cls="text-red-600" />
            <Stat label="Conto" value={batch.conto || "—"} small />
          </div>

          <div className="flex gap-2 mb-4">
            <Button variant={view === "abbinamento" ? "default" : "outline"} size="sm" onClick={() => setView("abbinamento")} data-testid="ric-view-abbinamento">Abbinamento entrate</Button>
            <Button variant={view === "movimenti" ? "default" : "outline"} size="sm" onClick={() => setView("movimenti")} data-testid="ric-view-movimenti">Tutti i movimenti</Button>
          </div>

          {view === "abbinamento" ? <Abbinamento batchId={batch.id} /> : <Movimenti batchId={batch.id} />}
        </div>
      )}
    </div>
  );
}

function QuadraturaBadge({ batchId, refreshKey }) {
  const [q, setQ] = useState(null);
  useEffect(() => { api.get(`/contabilita/riconciliazione/batch/${batchId}/quadratura`).then((r) => setQ(r.data)).catch(() => setQ(null)); }, [batchId, refreshKey]);
  if (!q) return null;
  const ok = q.quadra;
  return (
    <div data-testid="ric-quadratura" className={`rounded-xl border p-4 mb-4 ${ok ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          {ok ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <RefreshCw className="h-5 w-5 text-amber-600" />}
          <span className={`font-heading font-medium ${ok ? "text-emerald-700" : "text-amber-700"}`}>
            {ok ? "Estratto quadrato: tutte le entrate riconciliate" : `Riconciliato ${q.perc_riconciliato}% delle entrate`}
          </span>
        </div>
        <div className="flex items-center gap-5 text-sm">
          <span className="text-slate-600">Riconciliato: <b className="text-emerald-600">{euro(q.tot_riconciliato)}</b></span>
          <span className="text-slate-600">Da riconciliare: <b className="text-amber-600">{euro(q.tot_da_riconciliare)}</b></span>
          <span className="text-slate-600">Saldo netto: <b className={q.saldo_netto >= 0 ? "text-emerald-600" : "text-red-600"}>{euro(q.saldo_netto)}</b></span>
        </div>
      </div>
      {q.assieasy_tot_bancario != null && (
        <div className="mt-2 pt-2 border-t border-black/5 text-xs text-slate-500">
          Confronto Assieasy (incassi bonifico/POS/RID del periodo): <b>{euro(q.assieasy_tot_bancario)}</b> vs entrate banca <b>{euro(q.tot_entrate)}</b> · scostamento <b className={Math.abs(q.scostamento_assieasy) < 1 ? "text-emerald-600" : "text-slate-700"}>{euro(q.scostamento_assieasy)}</b>
          <span className="text-slate-400"> (le entrate banca includono anche giroconti e accrediti non assicurativi)</span>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub, cls = "text-slate-900", small }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className={`font-heading font-semibold ${small ? "text-sm" : "text-2xl"} ${cls}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function Abbinamento({ batchId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = () => { setLoading(true); api.get(`/contabilita/riconciliazione/batch/${batchId}/match`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false)); };
  useEffect(() => { run(); }, [batchId]);

  const abbina = async (movId, cand) => {
    let marca = false;
    if (cand.fonte === "titolo") marca = window.confirm("Segnare anche il titolo come INCASSATO (bonifico)?");
    try { await api.post(`/contabilita/riconciliazione/movimenti/${movId}/abbina`, { ref_id: cand.ref_id, fonte: cand.fonte, marca_incassato: marca }); toast.success("Movimento riconciliato"); run(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const ignora = async (movId) => { await api.post(`/contabilita/riconciliazione/movimenti/${movId}/ignora`); run(); };

  const abbinaTutti = async () => {
    if (!data?.n_auto_match) return;
    const marca = window.confirm(`Confermare ${data.n_auto_match} abbinamenti automatici (≥75%)?\n\nOK = segna anche i titoli di Portafoglio come INCASSATO (bonifico).\nAnnulla = abbina senza toccare lo stato dei titoli.`);
    try {
      const { data: r } = await api.post(`/contabilita/riconciliazione/batch/${batchId}/abbina-auto?marca_incassato=${marca}`);
      toast.success(`${r.abbinati} movimenti riconciliati automaticamente`);
      run();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  if (loading) return <div className="text-slate-400 text-sm py-6">Calcolo abbinamenti…</div>;
  if (!data) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <p className="text-sm text-slate-600"><b>{data.n_da_riconciliare}</b> entrate da riconciliare · <b className="text-emerald-600">{data.n_auto_match}</b> con abbinamento suggerito ad alta probabilità</p>
        <div className="flex items-center gap-2">
          {data.n_auto_match > 0 && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={abbinaTutti} data-testid="ric-abbina-tutti">
              <Link2 className="h-3.5 w-3.5 mr-1" />Abbina tutti gli auto-match ({data.n_auto_match})
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={run} data-testid="ric-rerun"><RefreshCw className="h-3.5 w-3.5 mr-1" />Ricalcola</Button>
        </div>
      </div>
      {data.movimenti.length === 0 ? (
        <div className="text-center py-12 rounded-xl border border-dashed border-slate-300 bg-white text-slate-500" data-testid="ric-abbinamento-empty">
          <CheckCircle2 className="h-6 w-6 mx-auto mb-2 text-emerald-500" />Tutte le entrate sono riconciliate o ignorate.
        </div>
      ) : (
        <div className="space-y-3">
          {data.movimenti.map(({ movimento: m, suggerimenti, auto_match }) => (
            <div key={m.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`ric-mov-${m.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-emerald-600">{euro(m.importo)}</span>
                    <span className="text-xs text-slate-400">{fmtIT(m.data)}</span>
                    <Badge variant="secondary" className="bg-slate-100 text-slate-600 text-[10px]">{m.etichetta}</Badge>
                  </div>
                  <div className="text-sm text-slate-700 mt-1 font-medium">{m.operazione}</div>
                  <div className="text-xs text-slate-400 mt-0.5 line-clamp-2">{m.dettagli}</div>
                </div>
                <Button variant="ghost" size="sm" className="text-slate-400 shrink-0" onClick={() => ignora(m.id)} data-testid={`ric-ignora-${m.id}`}><X className="h-4 w-4 mr-1" />Ignora</Button>
              </div>
              <div className="mt-3 border-t border-slate-100 pt-3">
                {suggerimenti.length === 0 ? (
                  <div className="text-xs text-slate-400">Nessun incasso corrispondente in portafoglio.</div>
                ) : (
                  <div className="space-y-1.5">
                    {suggerimenti.map((s) => (
                      <div key={`${s.fonte}-${s.ref_id}`} className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 ${auto_match && auto_match.ref_id === s.ref_id ? "bg-emerald-50 border border-emerald-200" : "bg-slate-50"}`} data-testid={`ric-sugg-${m.id}-${s.ref_id}`}>
                        <div className="min-w-0 text-sm">
                          <Badge variant="secondary" className={`text-[9px] mr-1.5 ${s.fonte === "assieasy" ? "bg-blue-100 text-blue-700" : "bg-violet-100 text-violet-700"}`}>{s.fonte === "assieasy" ? "Assieasy" : "Portafoglio"}</Badge>
                          <span className="font-medium text-slate-800">{s.nome || "—"}</span>
                          <span className="text-slate-400"> · {s.compagnia ? s.compagnia + " · " : ""}pol. {s.polizza || "—"}{s.targa ? ` · ${s.targa}` : ""} · {euro(s.importo)}</span>
                          <span className="ml-2 text-[11px] text-slate-400">{s.reasons.join(", ")}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge className={`text-[10px] ${s.score >= 75 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{s.score}%</Badge>
                          <Button size="sm" className="h-8" onClick={() => abbina(m.id, s)} data-testid={`ric-abbina-${m.id}-${s.ref_id}`}><Link2 className="h-3.5 w-3.5 mr-1" />Abbina</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Movimenti({ batchId }) {
  const [rows, setRows] = useState([]);
  const [tipo, setTipo] = useState("all");
  const load = () => { const p = {}; if (tipo !== "all") p.tipo = tipo; api.get(`/contabilita/riconciliazione/batch/${batchId}/movimenti`, { params: p }).then((r) => setRows(r.data)).catch(() => setRows([])); };
  useEffect(() => { load(); }, [batchId, tipo]);
  const setEt = async (id, et) => { await api.post(`/contabilita/riconciliazione/movimenti/${id}/etichetta`, { etichetta: et }); load(); };
  const STATO = { da_riconciliare: "Da riconciliare", riconciliato: "Riconciliato", ignorato: "Ignorato", categorizzato: "Categorizzato" };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Label className="text-sm">Tipo</Label>
        <Select value={tipo} onValueChange={setTipo}><SelectTrigger className="w-40" data-testid="ric-mov-tipo"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="entrata">Entrate</SelectItem><SelectItem value="uscita">Uscite</SelectItem></SelectContent></Select>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Operazione</TableHead><TableHead>Importo</TableHead><TableHead>Etichetta</TableHead><TableHead>Stato</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((m) => (
              <TableRow key={m.id} data-testid={`ric-movrow-${m.id}`}>
                <TableCell className="whitespace-nowrap text-xs">{fmtIT(m.data)}</TableCell>
                <TableCell className="max-w-[360px]"><div className="truncate font-medium text-slate-700">{m.operazione}</div><div className="truncate text-xs text-slate-400">{m.dettagli}</div></TableCell>
                <TableCell className={m.importo >= 0 ? "text-emerald-600 whitespace-nowrap" : "text-red-600 whitespace-nowrap"}>{euro(m.importo)}</TableCell>
                <TableCell>
                  <Select value={m.etichetta} onValueChange={(v) => setEt(m.id, v)}><SelectTrigger className="h-8 w-56 text-xs" data-testid={`ric-et-${m.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ETICHETTE.map((e) => <SelectItem key={e} value={e} className="text-xs">{e}</SelectItem>)}</SelectContent></Select>
                </TableCell>
                <TableCell><span className="text-xs text-slate-500">{STATO[m.stato] || m.stato}</span></TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Nessun movimento.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
